import { createSelectorFunctions } from 'auto-zustand-selectors-hook'
import { create } from 'zustand'

/**
 * TCS-8180: CMS props for the cookie-consent feature (banner + preferences
 * center), matching the Liferay structure layout:
 *
 * - bannerConsentTexts: banner texts
 * - configureCookiesModalTexts: preferences modal texts + the cookieCards
 *   repeatable field (mapped directly to the design-system CookieCard
 *   interface — the business can add/edit categories without a FE change).
 *
 * Inner field names stay prefixed (banner* / modal*) because Liferay does not
 * allow duplicate field names within the same structure, even across
 * different nested objects.
 *
 * All texts default to '' / [] — the display components apply hardcoded
 * fallbacks per brand when a field is empty, so the feature works before the
 * Liferay article is placed on the page.
 */

/** One repeatable Liferay card entry, mapped to the design-system CookieCard */
export interface ICookieConsentCard {
  /** Unique card id (also used to persist the toggle state) */
  id: number
  /** Card title (e.g. 'Essenciais') */
  title: string
  /** Card description */
  description: string
  /** Consent key persisted and sent to the API (e.g. 'analytics_storage') */
  gtmName: string
  /** 'essential' cards are locked on; 'optional' cards are user-controlled */
  type: 'essential' | 'optional'
  /** Optional badge label (e.g. 'Essencial') */
  badgeLabel?: string
  /** Optional badge icon name (design-system icons) */
  badgeIcon?: string
  /** Optional initial toggle state for optional cards (default: false) */
  toggleButtonValue?: boolean
  /** Optional: disable the card toggle in the UI (essential cards are always disabled) */
  isDisabled?: boolean
}

/** Banner texts (bannerConsentTexts group in the CMS) */
export interface IBannerConsentTexts {
  bannerTitle: string
  bannerDescription: string
  bannerAcceptAllLabel: string
  bannerRefuseNonEssentialLabel: string
  bannerManageLabel: string
}

/**
 * Preferences modal texts (configureCookiesModalTexts group in the CMS).
 * The cookie cards repeatable field lives inside this group in the Liferay
 * structure, so it is part of this interface.
 */
export interface IConfigureCookiesModalTexts {
  modalTitle: string
  modalSubtitle: string
  modalSaveLabel: string
  modalDisclaimer: string
  /** Consent category cards (repeatable Liferay field) */
  cookieCards: ICookieConsentCard[]
}

export interface ICookieConsentProps {
  /** CMS kill-switch: render the banner at all (defaults to true) */
  showCookieConsent: boolean

  /** Banner texts */
  bannerConsentTexts: IBannerConsentTexts

  /** Preferences modal texts + cookie cards */
  configureCookiesModalTexts: IConfigureCookiesModalTexts

  /**
   * Cookie policy version from the CMS (e.g. '1.0.0'). The business bumps it
   * in Liferay whenever the cookies policy portal is updated. Persisted with
   * the consent record and sent to the consent API (follow-up ticket).
   */
  cookiePolicyVersion: string
}

export interface CookieConsentPropsStore extends ICookieConsentProps {
  setProps: (props: ICookieConsentProps) => void
}

const DEFAULTS: ICookieConsentProps = {
  showCookieConsent: true,

  bannerConsentTexts: {
    bannerTitle: '',
    bannerDescription: '',
    bannerAcceptAllLabel: '',
    bannerRefuseNonEssentialLabel: '',
    bannerManageLabel: ''
  },

  configureCookiesModalTexts: {
    modalTitle: '',
    modalSubtitle: '',
    modalSaveLabel: '',
    modalDisclaimer: '',
    cookieCards: []
  },

  cookiePolicyVersion: ''
}

export const useCookieConsentPropsStore = createSelectorFunctions(
  create<CookieConsentPropsStore>((set) => ({
    ...DEFAULTS,

    setProps: (props: ICookieConsentProps) => set(props)
  }))
)
